import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';

export default function WantsChat() {
  const { conversationId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [conversations, setConversations] = useState([]);
  const [allConversations, setAllConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [error, setError] = useState(null);
  const [hiddenConvs, setHiddenConvs] = useState(() => {
    try {
      const saved = localStorage.getItem('hiddenConvs');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const messagesEndRef = useRef(null);
  const shouldScrollRef = useRef(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useLayoutEffect(() => {
    if (!isClient) return;
    const header = document.querySelector('header');
    if (header) setHeaderHeight(header.offsetHeight);
  }, [isClient]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchConversations();
    const interval = setInterval(() => fetchConversations(), 30000);
    return () => clearInterval(interval);
  }, [user, navigate]);

  useEffect(() => {
    if (conversationId) {
      fetchConversation(conversationId);
    } else {
      setActiveConv(null);
      setMessages([]);
    }
  }, [conversationId]);

  useEffect(() => {
    if (shouldScrollRef.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      shouldScrollRef.current = false;
    }
  }, [messages]);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.get('/wants-conversations');
      const convs = response.data || [];
      setAllConversations(convs);
      setConversations(convs);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchConversation = useCallback(async (id) => {
    setRefreshing(true);
    setError(null);
    shouldScrollRef.current = true;
    
    try {
      const response = await api.get(`/wants-conversations/${id}`);
      if (response.data) {
        setActiveConv(response.data);
        setMessages(response.data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching conversation:', err);
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  }, []);

  const hideConversation = (convId, e) => {
    if (e) e.stopPropagation();
    if (!confirm('¿Ocultar esta conversación?')) return;
    
    const newHidden = [...hiddenConvs, convId];
    setHiddenConvs(newHidden);
    localStorage.setItem('hiddenConvs', JSON.stringify(newHidden));
    setConversations(prev => prev.filter(c => c.id !== convId));
    if (conversationId === convId) {
      navigate('/wants-chat');
    }
  };

  const unhideConversation = (convId) => {
    if (!confirm('¿Mostrar esta conversación?')) return;
    
    const newHidden = hiddenConvs.filter(id => id !== convId);
    setHiddenConvs(newHidden);
    localStorage.setItem('hiddenConvs', JSON.stringify(newHidden));
    setShowHidden(false);
    // Force refresh the conversations list
    setConversations([...conversations]);
  };

  const removeHiddenConversation = async (convId, e) => {
    if (e) e.stopPropagation();
    if (!confirm('¿Eliminar esta conversación definitivamente?')) return;
    
    try {
      const apiUrl = `/api`;
      const token = localStorage.getItem('token');
      
      await fetch(`${apiUrl}/wants-conversations/${convId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Always remove from local list regardless of server response
      const newHidden = hiddenConvs.filter(id => id !== convId);
      setHiddenConvs(newHidden);
      localStorage.setItem('hiddenConvs', JSON.stringify(newHidden));
      setAllConversations(prev => prev.filter(c => c.id !== convId));
    } catch (err) {
      console.error('Error deleting conversation:', err);
      // Still remove from local list
      const newHidden = hiddenConvs.filter(id => id !== convId);
      setHiddenConvs(newHidden);
      localStorage.setItem('hiddenConvs', JSON.stringify(newHidden));
      setAllConversations(prev => prev.filter(c => c.id !== convId));
    }
  };

  const clearAllHidden = async () => {
    if (hiddenCount === 0) return;
    if (!confirm('¿Eliminar todas las conversaciones ocultas?')) return;
    
    const apiUrl = `/api`;
    const token = localStorage.getItem('token');
    
    try {
      for (const convId of hiddenConvs) {
        await fetch(`${apiUrl}/wants-conversations/${convId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (err) {
      console.error('Errors deleting conversations (continuing anyway):', err);
    }
    
    // Always clear local list
    const newHidden = [];
    setHiddenConvs(newHidden);
    localStorage.setItem('hiddenConvs', JSON.stringify(newHidden));
    setAllConversations([]);
    setConversations([]);
    setShowHidden(false);
    alert('Lista limpiada');
  };

  const sendMessage = async () => {
    const text = newMessage.trim();
    if (!text || !conversationId) return;
    
    setNewMessage('');
    setSending(true);
    
    try {
      const response = await api.post(`/wants-conversations/${conversationId}/messages`, { content: text });
      const data = response.data;
      if (data) {
        setMessages(prev => [...prev, data]);
        shouldScrollRef.current = true;
        fetchConversations();
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setNewMessage(text);
      alert('Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return d.toLocaleDateString('es-CR', { month: 'short', day: 'numeric' });
  };

  const hiddenCount = allConversations.filter(c => hiddenConvs.includes(c.id) && c.unread_count === 0).length;
  const hiddenWithNewMessages = allConversations.filter(c => hiddenConvs.includes(c.id) && c.unread_count > 0);
  const visibleConversations = showHidden ? conversations : conversations.filter(c => !hiddenConvs.includes(c.id) || c.unread_count > 0);

  return (
    <div className="flex bg-white overflow-hidden" style={{height: `calc(100vh - ${headerHeight}px)`}}>
      {/* Conversations List */}
      <div className="w-80 bg-gray-50 border-r flex flex-col min-h-0">
        <div className="px-4 py-3 border-b bg-gray-100 flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            💬 Mensajes
            {conversations.filter(c => c.unread_count > 0).length > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {conversations.filter(c => c.unread_count > 0).length}
              </span>
            )}
          </h2>
          {hiddenCount > 0 && (
            <button onClick={() => setShowHidden(!showHidden)} className="text-sm text-gray-500 hover:text-gray-700">
              {showHidden ? '🔽' : `${hiddenCount} ocultas 🔼`}
            </button>
          )}
        </div>
        
        <div className="flex-1 overflow-auto">
          {hiddenWithNewMessages.length > 0 && (
            <div className="bg-yellow-50 border-b">
              <div className="px-3 py-2 text-sm text-yellow-700 font-medium">📨 Nuevos mensajes</div>
              {hiddenWithNewMessages.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => { unhideConversation(conv.id); navigate(`/wants-chat/${conv.id}`); }}
                  className="w-full px-3 py-3 flex items-center gap-3 hover:bg-yellow-100 border-b text-left"
                >
                  <div className="w-10 h-10 bg-yellow-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {conv.other_user?.name?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{conv.other_user?.name || 'Usuario'}</p>
                    <p className="text-sm text-gray-500 truncate">{conv.last_message?.content || '...'}</p>
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {conv.unread_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="p-4 text-center text-gray-500 text-sm">Cargando...</div>
          ) : visibleConversations.length === 0 && hiddenWithNewMessages.length === 0 ? (
            <div className="p-6 text-center text-gray-400">
              <div className="text-4xl mb-2">💬</div>
              <p className="text-sm">Sin conversaciones</p>
            </div>
          ) : (
            visibleConversations.map(conv => (
              <div key={conv.id} className="relative group">
                <button
                  onClick={() => navigate(`/wants-chat/${conv.id}`)}
                  className={`w-full px-3 py-3 flex items-center gap-3 hover:bg-gray-100 border-b text-left ${
                    conversationId === conv.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                    {conv.other_user?.name?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between">
                      <p className="text-sm font-semibold truncate">{conv.other_user?.name || 'Usuario'}</p>
                      {conv.last_message && (
                        <span className="text-xs text-gray-400">{formatTime(conv.last_message.created_at)}</span>
                      )}
                    </div>
                    {conv.want && (
                      <p className="text-sm text-primary truncate">🔍 {conv.want.title}</p>
                    )}
                    {conv.product && (
                      <p className="text-sm text-green-600 truncate">📦 {conv.product.title}</p>
                    )}
                    <p className="text-sm text-gray-500 truncate">{conv.last_message?.content || '...'}</p>
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center shrink-0">
                      {conv.unread_count}
                    </span>
                  )}
                </button>
                <button
                  onClick={(e) => hideConversation(conv.id, e)}
                  className="absolute top-1 right-1 p-1 bg-gray-200 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition text-xs"
                  title="Ocultar"
                >
                  ✕
                </button>
              </div>
            ))
          )}
        </div>

        {showHidden && hiddenCount > 0 && (
          <div className="border-t bg-gray-100 max-h-32 overflow-auto">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-sm text-gray-500 font-medium">Conversaciones ocultas ({hiddenCount})</span>
              <button onClick={clearAllHidden} className="text-xs text-red-500 hover:text-red-700 font-bold">
                🗑️ Eliminar todas
              </button>
            </div>
            {allConversations
              .filter(c => hiddenConvs.includes(c.id) && c.unread_count === 0)
              .map(conv => (
                <div key={conv.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-200 text-sm">
                  <span className="truncate flex-1">{conv.other_user?.name}</span>
                  <button onClick={() => unhideConversation(conv.id)} className="text-green-600 font-bold">👁</button>
                  <button onClick={(e) => removeHiddenConversation(conv.id, e)} className="text-red-500 font-bold">✕</button>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-gray-100 min-h-0">
        {error && (
          <div className="bg-red-100 text-red-700 p-4 text-center">
            Error: {error}
          </div>
        )}
        
        {refreshing ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-gray-500">Cargando...</div>
          </div>
        ) : activeConv ? (
          <>
            {/* Header */}
            <div className="px-3 py-3 bg-white border-b flex items-center gap-3 shadow-sm">
              <button onClick={() => navigate('/wants-chat')} className="p-2 hover:bg-gray-100 rounded text-lg">
                ←
              </button>
              <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center text-base font-bold">
                {activeConv.other_user?.name?.charAt(0) || '?'}
              </div>
              <div className="flex-1">
                <p className="text-base font-semibold">{activeConv.other_user?.name || 'Usuario'}</p>
                {activeConv.want && (
                  <p className="text-sm text-primary truncate">🔍 {activeConv.want.title}</p>
                )}
                {activeConv.product && (
                  <p className="text-sm text-green-600 truncate">📦 {activeConv.product.title}</p>
                )}
              </div>
              <button onClick={() => fetchConversation(conversationId)} className="p-2 hover:bg-gray-100 rounded text-lg">
                ↻
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {messages.length === 0 ? (
                <div className="text-center text-gray-400 py-10">
                  <div className="text-5xl mb-3">💬</div>
                  <p className="text-base">No hay mensajes</p>
                  <p className="text-sm mt-1">Escribe abajo para comenzar</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isOwn = msg.sender_id === user?.id;
                  return (
                    <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-4 py-3 rounded-2xl text-base shadow ${
                        isOwn ? 'bg-primary text-white rounded-br-none' : 'bg-white rounded-bl-none'
                      }`}>
                        <p className="text-base">{msg.content}</p>
                        <p className={`text-xs mt-1 ${isOwn ? 'text-red-200' : 'text-gray-400'}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-4 py-3 bg-white border-t flex gap-3">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Escribe un mensaje..."
                className="flex-1 border-2 border-gray-200 rounded-full px-5 py-3 text-base focus:outline-none focus:border-primary"
                disabled={sending}
              />
              <button
                onClick={sendMessage}
                disabled={sending || !newMessage.trim()}
                className="w-12 h-12 bg-primary hover:bg-primary-dark disabled:bg-gray-300 text-white rounded-full flex items-center justify-center text-xl shadow"
              >
                {sending ? '...' : '➤'}
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 bg-gray-50">
            <div className="text-center">
              <div className="text-6xl mb-3 opacity-30">💬</div>
              <p className="text-lg">Selecciona una conversación</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}